// src/pages/AdminAnalyticsPage.js
import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  LinearProgress,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  TrendingUp,
  AccessTime,
  VideoLibrary,
  Work,
  Person,
  Refresh,
  Analytics,
  Storage,
  Visibility,
  Download,
  Timeline,
  Language,
  Schedule,
  Login,
  CheckCircle,
  Cancel,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/common/DashboardLayout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import COLORS from '../utils/color';

// Import our new analytics service
import analyticsService from '../services/analyticsService';

const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(4),
  flexDirection: 'row',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const MainTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: COLORS.text.primary,
  marginBottom: theme.spacing(1),
  background: `linear-gradient(135deg, ${COLORS.text.primary} 0%, ${COLORS.primary.light} 100%)`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontSize: '2.5rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: COLORS.gradients.card,
  border: `1px solid ${COLORS.border.primary}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  height: '100%',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: COLORS.shadows.card,
  },
}));

const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '2.5rem',
  fontWeight: 700,
  color: COLORS.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const MetricLabel = styled(Typography)(({ theme }) => ({
  color: COLORS.text.secondary,
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: theme.spacing(1),
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  background: COLORS.gradients.card,
  borderRadius: '16px',
  border: `1px solid ${COLORS.border.primary}`,
  overflow: 'hidden',
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: 40,
  height: 40,
  backgroundColor: COLORS.primary.main,
  fontSize: '16px',
  fontWeight: 600,
}));

const EngagementBar = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  width: 60,
  height: 6,
  borderRadius: 3,
  backgroundColor: COLORS.border.primary,
  '& .MuiLinearProgress-bar': {
    borderRadius: 3,
  },
}));

const AdminAnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  // State management
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Data states
  const [usersAnalytics, setUsersAnalytics] = useState(null);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  
  // Filter states
  const [sortBy, setSortBy] = useState('total_time');
  const [days, setDays] = useState(30);
  const [page, setPage] = useState(1);
  
  // Modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  // Check admin access
  useEffect(() => {
    if (!loading && !isAdmin()) {
      navigate('/admin');
      showError('Admin access required');
    }
  }, [loading, isAdmin, navigate, showError]);

  // Load data when filters change
  useEffect(() => {
    loadAnalyticsData();
  }, [selectedTab, sortBy, days, page]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (selectedTab === 0) {
        // Load users analytics
        const response = await analyticsService.getAllUsersAnalytics(page, 20, sortBy, days);
        setUsersAnalytics(response);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to load analytics data';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    setPage(1); // Reset pagination
  };

  const handleRefresh = () => {
    loadAnalyticsData();
    showSuccess('Analytics data refreshed');
  };

  const handleViewUserDetails = async (userId) => {
    try {
      setLoading(true);
      const userDetails = await analyticsService.getUserAnalytics(userId, days);
      setSelectedUserDetails(userDetails);
      setDetailModalOpen(true);
    } catch (error) {
      showError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      if (selectedTab === 0 && usersAnalytics) {
        // Export users data
        const exportData = usersAnalytics.users.map(userAnalytics => ({
          email: userAnalytics.user.email,
          role: userAnalytics.user.role,
          status: userAnalytics.user.status,
          total_time_hours: Math.round(userAnalytics.metrics.total_time_seconds / 3600 * 100) / 100,
          total_sessions: userAnalytics.metrics.total_sessions,
          total_videos: userAnalytics.metrics.total_videos,
          total_jobs: userAnalytics.metrics.total_jobs,
          avg_session_minutes: Math.round(userAnalytics.metrics.avg_session_duration / 60),
          created_at: new Date(userAnalytics.user.created_at).toLocaleDateString(),
          last_login: userAnalytics.user.last_login 
            ? new Date(userAnalytics.user.last_login).toLocaleDateString() 
            : 'Never'
        }));
        
        await analyticsService.exportToCSV(exportData, `user_analytics_${days}days_${new Date().toISOString().split('T')[0]}.csv`);
        showSuccess('User analytics exported successfully');
      } 
    } catch (error) {
      showError('Failed to export data');
    }
  };

  const getEngagementLevel = (metrics) => {
    const score = Math.round(
      (metrics.total_time_seconds / 3600) * 20 + 
      metrics.total_sessions * 2 + 
      (metrics.total_videos + metrics.total_jobs) * 3
    );
    
    if (score >= 80) return { level: 'High', color: COLORS.status.success, progress: Math.min(score, 100) };
    if (score >= 50) return { level: 'Medium', color: COLORS.status.warning, progress: score };
    return { level: 'Low', color: COLORS.status.error, progress: score };
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  if (!isAdmin()) {
    return null; // Will redirect
  }

  return (
    <DashboardLayout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <HeaderSection>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <IconButton onClick={() => navigate('/admin')} sx={{ color: COLORS.text.secondary }}>
                <ArrowBack />
              </IconButton>
              <MainTitle>User Analytics</MainTitle>
            </Box>
            <Typography variant="h6" sx={{ color: COLORS.text.secondary }}>
              Comprehensive insights into user behavior and platform usage
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Period</InputLabel>
              <Select
                value={days}
                onChange={(e) => setDays(e.target.value)}
                label="Period"
              >
                <MenuItem value={7}>Last 7 days</MenuItem>
                <MenuItem value={30}>Last 30 days</MenuItem>
                <MenuItem value={90}>Last 90 days</MenuItem>
                <MenuItem value={365}>Last year</MenuItem>
              </Select>
            </FormControl>

            {selectedTab === 0 && (
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="total_time">Time Spent</MenuItem>
                  <MenuItem value="sessions">Sessions</MenuItem>
                  <MenuItem value="videos">Videos</MenuItem>
                  <MenuItem value="jobs">Jobs</MenuItem>
                </Select>
              </FormControl>
            )}

            <Button
              variant="outlined"
              onClick={handleRefresh}
              startIcon={<Refresh />}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              startIcon={<Download />}
              disabled={loading || (!usersAnalytics)}
            >
              Export
            </Button>
          </Box>
        </HeaderSection>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Person />
                  User Analytics
                  {usersAnalytics && (
                    <Chip 
                      label={usersAnalytics.pagination.total_users} 
                      size="small" 
                      color="primary"
                    />
                  )}
                </Box>
              }
            />
            <Tab 
            
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {selectedTab === 0 && (
          <Box>
            {/* Quick Stats */}
            {usersAnalytics && (
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent>
                      <MetricLabel>Total Users</MetricLabel>
                      <MetricValue>{usersAnalytics.pagination.total_users}</MetricValue>
                      <Typography variant="body2" color="text.secondary">
                        Active users
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent>
                      <MetricLabel>Combined Usage</MetricLabel>
                      <MetricValue>
                        {formatDuration(
                          usersAnalytics.users.reduce((total, user) => 
                            total + user.metrics.total_time_seconds, 0
                          )
                        )}
                      </MetricValue>
                      <Typography variant="body2" color="text.secondary">
                        Total time spent
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent>
                      <MetricLabel>Content Created</MetricLabel>
                      <MetricValue>
                        {usersAnalytics.users.reduce((total, user) => 
                          total + user.metrics.total_videos, 0
                        )}
                      </MetricValue>
                      <Typography variant="body2" color="text.secondary">
                        Videos uploaded
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard>
                    <CardContent>
                      <MetricLabel>Jobs Processed</MetricLabel>
                      <MetricValue>
                        {usersAnalytics.users.reduce((total, user) => 
                          total + user.metrics.total_jobs, 0
                        )}
                      </MetricValue>
                      <Typography variant="body2" color="text.secondary">
                        Dubbing jobs
                      </Typography>
                    </CardContent>
                  </MetricCard>
                </Grid>
              </Grid>
            )}

            {/* Users Table */}
            {usersAnalytics && (
              <StyledTableContainer component={Paper}>
                <Table>
                  <TableHead sx={{ backgroundColor: COLORS.overlay.light }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Usage Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Sessions</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Content</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Engagement</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Last Activity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usersAnalytics.users.map((userAnalytics) => {
                      const engagement = getEngagementLevel(userAnalytics.metrics);
                      
                      return (
                        <TableRow 
                          key={userAnalytics.user.id}
                          sx={{ '&:hover': { backgroundColor: COLORS.hover.background } }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <UserAvatar>
                                {userAnalytics.user.email.charAt(0).toUpperCase()}
                              </UserAvatar>
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                  {userAnalytics.user.email}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  <Chip 
                                    label={userAnalytics.user.role} 
                                    size="small" 
                                    color={userAnalytics.user.role === 'admin' ? 'primary' : 'default'}
                                  />
                                  <Chip 
                                    label={userAnalytics.user.status} 
                                    size="small" 
                                    color="success"
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {formatDuration(userAnalytics.metrics.total_time_seconds)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Avg: {Math.round(userAnalytics.metrics.avg_session_duration / 60)}m per session
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6" sx={{ color: COLORS.primary.main, fontWeight: 600 }}>
                              {userAnalytics.metrics.total_sessions}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {userAnalytics.metrics.total_videos} videos
                            </Typography>
                            <Typography variant="body2">
                              {userAnalytics.metrics.total_jobs} jobs
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <EngagementBar>
                              <ProgressBar 
                                variant="determinate" 
                                value={engagement.progress} 
                                sx={{
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: engagement.color,
                                  },
                                }}
                              />
                              <Chip 
                                label={engagement.level}
                                size="small"
                                sx={{ 
                                  backgroundColor: `${engagement.color}20`,
                                  color: engagement.color,
                                  fontWeight: 600
                                }}
                              />
                            </EngagementBar>
                          </TableCell>
                          <TableCell>
                            {userAnalytics.recent_sessions.length > 0 ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {formatDuration(userAnalytics.recent_sessions[0].duration_seconds)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(userAnalytics.user.last_login).toLocaleDateString()}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No recent activity
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View detailed analytics">
                              <IconButton 
                                onClick={() => handleViewUserDetails(userAnalytics.user.id)}
                                sx={{ color: COLORS.primary.main }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </StyledTableContainer>
            )}

            {/* Pagination */}
            {usersAnalytics && usersAnalytics.pagination.total_pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
                <Button
                  disabled={!usersAnalytics.pagination.has_prev}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Page {usersAnalytics.pagination.page} of {usersAnalytics.pagination.total_pages}
                </Typography>
                <Button
                  disabled={!usersAnalytics.pagination.has_next}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        )}


        {/* User Detail Modal */}
        <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">User Analytics Details</Typography>
              <IconButton onClick={() => setDetailModalOpen(false)}>
                <Cancel />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedUserDetails && (
              <Box>
                {/* User Info Header */}
                <Card sx={{ mb: 3, backgroundColor: COLORS.overlay.light }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <UserAvatar sx={{ width: 48, height: 48 }}>
                        {selectedUserDetails.user.email.charAt(0).toUpperCase()}
                      </UserAvatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {selectedUserDetails.user.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Role: {selectedUserDetails.user.role} | 
                          Member since: {new Date(selectedUserDetails.user.created_at).toLocaleDateString()} |
                          Last Login: {new Date(selectedUserDetails.user.last_login).toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>

                {/* Metrics */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard>
                      <CardContent>
                        <MetricLabel>Total Time</MetricLabel>
                        <MetricValue sx={{ fontSize: '1.8rem' }}>
                          {selectedUserDetails.usage_summary.total_time_formatted}
                        </MetricValue>
                        <Typography variant="body2" color="text.secondary">
                          {selectedUserDetails.usage_summary.total_sessions} sessions
                        </Typography>
                      </CardContent>
                    </MetricCard>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard>
                      <CardContent>
                        <MetricLabel>Videos</MetricLabel>
                        <MetricValue sx={{ fontSize: '1.8rem' }}>
                          {selectedUserDetails.content_metrics.videos_uploaded}
                        </MetricValue>
                        <Typography variant="body2" color="text.secondary">
                          {selectedUserDetails.content_metrics.total_video_size_formatted}
                        </Typography>
                      </CardContent>
                    </MetricCard>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard>
                      <CardContent>
                        <MetricLabel>Jobs</MetricLabel>
                        <MetricValue sx={{ fontSize: '1.8rem' }}>
                          {selectedUserDetails.content_metrics.jobs_created}
                        </MetricValue>
                        <Typography variant="body2" color="text.secondary">
                          {selectedUserDetails.content_metrics.languages_count} languages
                        </Typography>
                      </CardContent>
                    </MetricCard>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <MetricCard>
                      <CardContent>
                        <MetricLabel>Avg Session</MetricLabel>
                        <MetricValue sx={{ fontSize: '1.8rem' }}>
                          {selectedUserDetails.usage_summary.avg_session_duration_formatted}
                        </MetricValue>
                        <Typography variant="body2" color="text.secondary">
                          per session
                        </Typography>
                      </CardContent>
                    </MetricCard>
                  </Grid>
                </Grid>

                {/* Recent Activity */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Recent Activity</Typography>
                {selectedUserDetails.recent_activity.map((activity, index) => (
                  <Card key={index} sx={{ mb: 1 }}>
                    <CardContent sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {activity.type.replace('_', ' ').toUpperCase()}
                          </Typography>
                          {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              {JSON.stringify(activity.metadata)}
                            </Typography>
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(activity.timestamp).toLocaleString()}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}

                {/* Languages Used */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>Languages Used</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedUserDetails.content_metrics.languages_used.map((lang, index) => (
                    <Chip
                      key={index}
                      label={lang.toUpperCase()}
                      icon={<Language />}
                      variant="outlined"
                      color="primary"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default AdminAnalyticsPage;